import { UserManager, WebStorageStateStore } from 'oidc-client-ts'
import { getEnvVariable } from '../utils/getEnvVariable'

const oidcScopes = 'openid email profile'

const getAudience = (): string => getEnvVariable('REACT_APP_AUTH0_AUDIENCE', '')
const getDomain = (): string => getEnvVariable('REACT_APP_AUTH0_DOMAIN', 'unset')
const getClientId = (): string => getEnvVariable('REACT_APP_AUTH0_CLIENT_ID', 'unset')

const getAuthority = (): string => {
  const domain = getDomain()
  return domain.startsWith('http') ? domain : `https://${domain}`
}

const REDIRECT_KEY = 'sa_oidc_redirecting'

interface OidcUser {
  sub?: string
  name?: string
  nickname?: string
  email?: string
  org_id?: string
}

const createUserManager = (organization: string): UserManager =>
  new UserManager({
    authority: getAuthority(),
    // eslint-disable-next-line camelcase
    client_id: getClientId(),
    // eslint-disable-next-line camelcase
    redirect_uri: window.location.origin + '/',
    scope: oidcScopes,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    extraQueryParams: {
      ...(organization ? { organization } : {}),
      ...(getAudience() ? { audience: getAudience() } : {}),
    },
  })

const ORG_STORAGE_KEY = 'sa_oidc_organization'
const ID_TOKEN_STORAGE_KEY = 'sa_oidc_id_token_hint'

let userManager: UserManager | null = null

export const isRedirecting = (): boolean => sessionStorage.getItem(REDIRECT_KEY) === 'true'

export const clearRedirectFlag = (): void => {
  const wasRedirecting = sessionStorage.getItem(REDIRECT_KEY) === 'true'
  sessionStorage.removeItem(REDIRECT_KEY)
  if (wasRedirecting) {
    // Back during redirect — reset so initOidc doesn't create a UserManager that triggers OIDC discovery loops.
    localStorage.removeItem(ORG_STORAGE_KEY)
    userManager = null
  }
}

// Empty `organization` is valid ("login started without a specific org"); a missing storage key means no login in flight — skip init.
export const initOidc = (organization?: string) => {
  if (userManager) return
  let org: string
  if (organization !== undefined) {
    org = organization
  } else {
    const stored = localStorage.getItem(ORG_STORAGE_KEY)
    if (stored === null) return
    org = stored
  }
  userManager = createUserManager(org)
}

export const handleCallback = async (): Promise<void> => {
  if (!userManager) {
    const stored = localStorage.getItem(ORG_STORAGE_KEY)
    if (stored === null) return
    userManager = createUserManager(stored)
  }
  sessionStorage.removeItem(REDIRECT_KEY)
  const user = await userManager.signinRedirectCallback()
  if (user?.access_token) {
    const tokenName = getEnvVariable('REACT_APP_AUTH_TOKEN', 'labs_token')
    localStorage.setItem(tokenName, `Bearer ${user.access_token}`)
  }
  if (user?.id_token) {
    localStorage.setItem(ID_TOKEN_STORAGE_KEY, user.id_token)
  }
  window.history.replaceState({}, document.title, window.location.origin + '/')
}

export const logout = async () => {
  localStorage.removeItem(ORG_STORAGE_KEY)
  const storedIdToken = localStorage.getItem(ID_TOKEN_STORAGE_KEY) ?? undefined
  localStorage.removeItem(ID_TOKEN_STORAGE_KEY)
  try {
    if (!userManager) {
      if (storedIdToken) {
        window.location.href = buildLogoutUrl(storedIdToken)
        return
      }
      window.location.href = window.location.origin + '/login'
      return
    }
    const existing = await userManager.getUser()
    await userManager.removeUser()
    const idTokenHint = existing?.id_token || storedIdToken
    await userManager.signoutRedirect({
      // eslint-disable-next-line camelcase
      id_token_hint: idTokenHint,
      // eslint-disable-next-line camelcase
      post_logout_redirect_uri: window.location.origin + '/',
    })
  } catch {
    window.location.href = window.location.origin + '/login'
  }
}

const buildLogoutUrl = (idTokenHint: string): string => {
  const params = new URLSearchParams({
    // eslint-disable-next-line camelcase
    id_token_hint: idTokenHint,
    // eslint-disable-next-line camelcase
    client_id: getClientId(),
    // eslint-disable-next-line camelcase
    post_logout_redirect_uri: window.location.origin + '/',
  })
  return `${getAuthority()}/protocol/openid-connect/logout?${params.toString()}`
}

export const getUser = async (): Promise<OidcUser | undefined> => {
  if (!userManager) return undefined
  const user = await userManager.getUser()
  if (!user) return undefined

  return {
    sub: user.profile.sub,
    name: user.profile.name,
    nickname: user.profile.nickname,
    email: user.profile.email,
    // eslint-disable-next-line camelcase
    org_id: (user.profile.org_id as string) || undefined,
  }
}

export const getAccessToken = async (): Promise<string | undefined> => {
  if (!userManager) return undefined
  const tokenName = getEnvVariable('REACT_APP_AUTH_TOKEN', 'labs_token')
  const user = await userManager.getUser()
  if (user && !user.expired) {
    localStorage.setItem(tokenName, `Bearer ${user.access_token}`)
    return user.access_token
  }
  try {
    const renewed = await userManager.signinSilent()
    if (renewed?.access_token) {
      localStorage.setItem(tokenName, `Bearer ${renewed.access_token}`)
    }
    // Only overwrite the stored hint when refresh actually returns a new id_token; preserve the login-time value as fallback.
    if (renewed?.id_token) {
      localStorage.setItem(ID_TOKEN_STORAGE_KEY, renewed.id_token)
    }
    return renewed?.access_token
  } catch {
    localStorage.removeItem(tokenName)
    return undefined
  }
}

export const getIsAuthenticated = async (): Promise<boolean> => {
  if (!userManager) return false
  const user = await userManager.getUser()
  return !!user && !user.expired
}

export const loginWithOrganization = (organization: string, email: string, idpHint?: string) => {
  localStorage.setItem(ORG_STORAGE_KEY, organization)
  sessionStorage.setItem(REDIRECT_KEY, 'true')
  userManager = createUserManager(organization)

  const extraQueryParams: Record<string, string> = {
    ...(getAudience() ? { audience: getAudience() } : {}),
    prompt: 'login',
  }
  if (idpHint) {
    extraQueryParams.organization = organization
    // eslint-disable-next-line camelcase
    extraQueryParams.kc_idp_hint = idpHint
  }

  void userManager.signinRedirect({
    // eslint-disable-next-line camelcase
    login_hint: email,
    extraQueryParams,
  })
}
